/**
 * Registers this app's What's New custom experiences (Tier C).
 * Side-effect import from App startup: `import './whatsNewExperiences';`
 *
 * Each experience is one registerExperience call. A tip references one by its
 * `experienceKey`; unknown/absent keys fall back to the default title/body card.
 */

import { lazy } from 'react';
import { registerExperience } from '@shared-ui/whats-new';

// Loaded when a tip that uses one is shown, not at startup: the report views
// bring KaTeX and the session report with them.
const SurveyExperience = lazy(() => import('@shared-ui/whats-new-experiences').then((m) => ({ default: m.SurveyExperience })));
const FeedbackReplyExperience = lazy(() => import('@shared-ui/whats-new-experiences').then((m) => ({ default: m.FeedbackReplyExperience })));
const QuickPollExperience = lazy(() => import('./QuickPollExperience').then((m) => ({ default: m.QuickPollExperience })));
const ChapterProgressExperience = lazy(() => import('./ChapterProgressExperience').then((m) => ({ default: m.ChapterProgressExperience })));
const AssignmentReportExperience = lazy(() => import('./AssignmentReportExperience').then((m) => ({ default: m.AssignmentReportExperience })));

registerExperience('quick-poll', {
    component: QuickPollExperience,
    ownsControls: true,   // the poll renders its own buttons
    title: 'Quick question',
});

registerExperience('survey', {
    component: SurveyExperience,
    ownsControls: true,       // renders its own Back / Next / Submit
    title: 'Quick survey',
    defersImpression: true,   // skips when the definition is empty/malformed
});

registerExperience('chapter-progress', {
    component: ChapterProgressExperience,
    ownsControls: true,       // renders its own practise CTA + "Maybe later"
    title: 'Your progress 📊',
    defersImpression: true,   // may load, find too little data, and skip
});

registerExperience('assignment-report', {
    component: AssignmentReportExperience,
    ownsControls: true,       // renders its own "See full report" CTA + "Maybe later"
    title: 'Your Assignment Report 📝',
    defersImpression: true,   // skips when no report payload came with the tip
});

// Code-owned card: the server synthesises it when an admin reply to this user's
// feedback was sent with "Show as pop-up" (functions/src/whatsNew/systemTips.ts).
registerExperience('feedback-reply', {
    component: FeedbackReplyExperience,
    ownsControls: true,       // renders its own "Open conversation" + "Got it"
    title: 'New reply to your feedback 💬',
    defersImpression: true,   // skips if the replies didn't come with the card
});
